<script lang="ts">
  import { flip } from 'svelte/animate';
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import type { GameMode } from '../../shared/modes';
  import { MODE_INFO } from '../content/modes';
  import ThemeIcon from '../themes/ThemeIcon.svelte';
  import TracedBoard from '../game/TracedBoard.svelte';
  import { tracedTiles } from '../game/board-trace';
  import { findPath } from '../../game/geometry';
  import RankChange from './RankChange.svelte';
  import StandingRow from './StandingRow.svelte';
  import { getAppearance } from '../themes/appearance.svelte';
  import type { SessionRoundResults as RoundResultsData } from '../session/room-session-state';
  import type { DailyStanding } from '../../shared/api';
  import {
    placingFor,
    placingKind,
    revealOrder,
    stageForPlayback,
    standingRanks,
    tallyForPlayback,
    type RevealPlayback,
  } from './reveal-stages';
  import { talliedScore, wordTally } from './word-reveal';
  import { recapPlan } from '../../game/recap';

  /**
   * One shared post-match presentation for every mode and both result sources.
   * Renders immutable completed-round data at a reveal stage; room actions,
   * fetching and transport stay with the caller.
   */
  interface Props {
    results: RoundResultsData;
    mode: GameMode;
    meId: string;
    playback: RevealPlayback;
    status?: string | null;
    primaryLabel?: string | null;
    onPrimary?: (() => void) | null;
    lobbyLabel?: string | null;
    onLobby?: (() => void) | null;
    leaderboardLabel?: string | null;
    onLeaderboard?: (() => void) | null;
    /** Jumps this viewer's recap to standings; absent where the result opens settled. */
    onSkip?: (() => void) | null;
    /** Daily only: the viewer's place on the day's leaderboard, once the round is archived. */
    dailyStanding?: DailyStanding | null;
  }
  let {
    results,
    mode,
    meId,
    playback,
    status = null,
    primaryLabel = null,
    onPrimary = null,
    lobbyLabel = null,
    onLobby = null,
    leaderboardLabel = null,
    onLeaderboard = null,
    onSkip = null,
    dailyStanding = null,
  }: Props = $props();

  /** Short enough that a row settles before the next few words move it again. */
  const STANDING_MOVE_MS = 250;

  const appearance = getAppearance();
  const reducedMotion = $derived(!appearance.motionAllowed);
  const stage = $derived(stageForPlayback(playback));
  const fraction = $derived(tallyForPlayback(playback, reducedMotion));
  const plan = $derived(recapPlan(results));
  const tally = $derived(wordTally(plan, stage, fraction, !reducedMotion));
  /** A chip tapped at standings: shown and traced in the longest word's place until tapped again. */
  let chipSelection = $state<{ roundId: string; playerId: string; w: string; pts: number } | null>(null);
  const selectedChip = $derived(
    stage === 'standings' && chipSelection?.roundId === results.roundId ? chipSelection : null,
  );
  const shownWord = $derived(tally.current ?? selectedChip ?? tally.longest);
  const showsLongest = $derived(selectedChip === null && tally.longest !== null);
  const scoredShown = $derived(tally.current !== null || selectedChip !== null);
  const scores = $derived(new Map(results.players.map((player) => [player.id, talliedScore(player, tally)])));
  const scoreOf = (player: { id: string }): number => scores.get(player.id)!;
  const ordered = $derived(revealOrder(results.players, scoreOf));
  const ranks = $derived(standingRanks(ordered, scoreOf));
  // Only head-to-head modes name a winner; solo recaps carry just the mode title.
  const headToHead = $derived(mode === 'versus' || mode === 'competitive');
  const placing = $derived(placingKind(mode));
  // Chaos tile changes can leave a word untraceable on the final board; it is then shown unhighlighted.
  const recapPath = $derived(shownWord ? (findPath(results.board, shownWord.w) ?? []) : []);
  const traced = $derived(tracedTiles(recapPath, tally.progress));
  const winners = $derived(
    results.players.filter((player) => player.score > 0 && player.score === results.players[0]?.score),
  );
  const rankChange = $derived(results.players.find((player) => player.id === meId)?.rankChange ?? null);
  // The standings show the winner visually (winner row, gold crown); screen readers hear it stated.
  const winnerAnnouncement = $derived(
    !headToHead
      ? null
      : winners.length > 1
        ? `It's a tie: ${winners.map((player) => player.name).join(' & ')}.`
        : winners[0]
          ? `${winners[0].name} wins.`
          : 'No points this round.',
  );
  const stageAnnouncement = $derived(
    stage === 'standings'
      ? ['Final standings.', winnerAnnouncement].filter(Boolean).join(' ')
      : stage === 'tally'
        ? 'Tallying the scores.'
        : 'Round complete. Revealing the results.',
  );
  // Without a replay action, the leaderboard is the screen's main next step.
  const leaderboardIsPrimary = $derived(!(primaryLabel && onPrimary));

  function selectChip(playerId: string, word: { w: string; pts: number }): void {
    const again = selectedChip?.playerId === playerId && selectedChip.w === word.w;
    chipSelection = again ? null : { roundId: results.roundId, playerId, w: word.w, pts: word.pts };
  }
</script>

<section id="screen-results" class="screen submenu" data-stage={stage}>
  <h1>{MODE_INFO[mode].title}</h1>
  <Card class="results">
    {#if stage !== 'standings' && onSkip}<Button
        class="recap-skip"
        aria-label="Skip Recap"
        title="Skip Recap"
        onclick={onSkip}><ThemeIcon name="skip" /></Button
      >{/if}
    <p class="sr-only" aria-live="polite">{stageAnnouncement}</p>
    <TracedBoard board={results.board} size={results.config.boardSize} {traced} />
    {#if stage !== 'standings' || shownWord}<div class="recap-word">
        {#if shownWord}{#key shownWord.w}{#if showsLongest}<span class="recap-side recap-side-start recap-tag"
                >Longest!</span
              >{/if}<span class="recap-current word-display">{shownWord.w}</span><span
              class="recap-side recap-side-end"
              ><span class="recap-points">{scoredShown ? '+' : ''}{shownWord.pts}</span></span
            >{/key}{/if}
      </div>{/if}
    {#if stage === 'standings' && rankChange}<RankChange change={rankChange} />{/if}
    <ol class="leaderboard">
      {#each ordered as player, index (player.id)}
        {@const winner = stage === 'standings' && headToHead && winners.some(({ id }) => id === player.id)}
        <li
          animate:flip={{ duration: reducedMotion ? 0 : STANDING_MOVE_MS }}
          class:first={winner}
          class:you={player.id === meId}
          class="lb"
        >
          <StandingRow
            {player}
            rankColumn={placing === 'rank'}
            placing={placingFor(placing, stage, ranks[index]!, player, dailyStanding)}
            {stage}
            you={player.id === meId}
            score={scoreOf(player)}
            classicScoring={results.config.scoringMode === 'classic'}
            selectedWord={selectedChip?.playerId === player.id ? selectedChip.w : null}
            selectWord={(word) => selectChip(player.id, word)}
          />
        </li>
      {/each}
    </ol>
    <div class="row">
      {#if primaryLabel && onPrimary}<Button variant="primary" size="big" full onclick={onPrimary}
          >{primaryLabel}</Button
        >{/if}
      {#if lobbyLabel && onLobby}<Button size="big" full onclick={onLobby}>{lobbyLabel}</Button>{/if}
      {#if leaderboardLabel && onLeaderboard}
        {#if leaderboardIsPrimary}<Button variant="primary" size="big" full onclick={onLeaderboard}
            >{leaderboardLabel}</Button
          >{:else}<Button variant="ghost" onclick={onLeaderboard}>{leaderboardLabel}</Button>{/if}
      {/if}
    </div>
    {#if status}<p class="status">{status}</p>{/if}
  </Card>
</section>

<style>
  #screen-results {
    animation: var(--theme-results-animation);
  }
  /* The recap card anchors the floating skip control in its top-right corner. */
  :global(.card.results) {
    position: relative;
  }
  :global(.btn.recap-skip) {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 10px;
    font-size: 20px;
  }
  /* Height is reserved from the intro so the first word does not shift the standings. The word
     keeps the full width; the side labels overlay it where a long word reaches them. */
  .recap-word {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 56px;
    margin-top: 8px;
    /* A transparent copy of the standings row border, so the points line up with row scores. */
    border: var(--theme-card-border);
    border-color: transparent;
  }
  .recap-current {
    font-size: 32px;
    line-height: 1.2;
    max-width: 100%;
    overflow-x: auto;
  }
  /* Points align with the standings' score column, inside the same border and 12px inline padding. */
  .recap-side {
    position: absolute;
    z-index: 1;
  }
  /* 12px row padding less the points' 4px backing padding keeps their digits on the score column. */
  .recap-side-end {
    right: 8px;
  }
  .recap-side-start {
    left: 12px;
  }
  /* The card-surface backing keeps the points readable where they overlay a long word. */
  .recap-points {
    padding: 0 4px;
    border-radius: var(--theme-chip-radius);
    background: var(--theme-surface);
    font-size: 18px;
    font-weight: 700;
    color: var(--theme-accent);
  }
  /* Marks the closing word as the board's longest, found or not; its points are what it is worth. */
  .recap-tag {
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 10px;
    line-height: 1.2;
    background: var(--theme-surface);
    color: var(--theme-muted-text);
  }
  .lb.you {
    border-color: var(--theme-accent);
    border-style: double;
  }
  /* The winner's whole row takes the pack's winner treatment, over the viewer's own marking. */
  .lb.first {
    background: var(--theme-winner-background);
    border: var(--theme-winner-border);
    box-shadow: var(--theme-winner-shadow);
  }
</style>
